// components/WhatsAppSendModal/WhatsAppSendModal.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import "../../css/WhatsappTemplates.css";
import {getActiveWhatsappTemplate, getWhatsappOrderLogs, sendManualWhatsappMsg} from "../../service/api/orders";

const WA_ICON = (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
);

const DRIVER_STATUSES = [3, 4, 5];

function getTargets(order) {
    const targets = ["CUSTOMER", "STORE"];
    if (DRIVER_STATUSES.includes(order?.orderstatus_id)) targets.push("DRIVER");
    return targets;
}

const TARGET_COLORS = {
    CUSTOMER: { bg: "#dbeafe", color: "#1e40af" },
    OWNER:    { bg: "#fef3c7", color: "#92400e" },
    STORE:    { bg: "#d1fae5", color: "#065f46" },
    DRIVER:   { bg: "#ede9fe", color: "#5b21b6" },
    ADMIN:    { bg: "#fee2e2", color: "#991b1b" },
    OTP:      { bg: "#fdf4ff", color: "#a21caf" },
    CANCEL:   { bg: "#fee2e2", color: "#991b1b" },
};
const targetColor = (t) => {
    if (t?.startsWith("STATUS")) return { bg: "#f0fdf4", color: "#15803d" };
    return TARGET_COLORS[t] || { bg: "#f1f5f9", color: "#475569" };
};

function formatTime(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            day: "2-digit", month: "short",
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    } catch { return iso; }
}

function injectPreview(body, order, reason) {
    const vars = {
        unique_order_id:        order?.unique_order_id   ?? "",
        customer_name:          order?.customer_name     ?? "",
        customer_phone:         order?.customer_phone    ?? "",
        restaurant_name:        order?.restaurant_name   ?? "",
        total:                  order?.order_total       ?? "",
        payment_mode:           order?.payment_mode      ?? "",
        actual_delivery_charge: order?.actual_delivery_charge ?? "",
        payable:                order?.order_total       ?? "",
        sub_total:              order?.sub_total         ?? "",
        wallet_amount:          order?.wallet_amount     ?? "",
        order_comment:          order?.order_comment     ?? "",
        reason:                 reason || "(reason)",
        items:                  "(order items)",
        target:                 "CUSTOMER",
    };
    let out = body;
    Object.entries(vars).forEach(([k, v]) => {
        out = out.replaceAll(`{${k}}`, v);
    });
    return out;
}

// ─── Log entry row ────────────────────────────────────────────────────────────
function LogEntry({ log }) {
    const c = targetColor(log.target);
    return (
        <div className={`wa-log-entry wa-log-entry--${log.status}`}>
            <div className="wa-log-entry-top">
                <span className="wa-log-tpl">{log.template_name}</span>
                <span className="wa-log-target" style={{ background: c.bg, color: c.color }}>
                    {log.target}
                </span>
                <span className={`wa-log-status wa-log-status--${log.status}`}>
                    {log.status === "sent" ? "✓ Sent" : "✕ Failed"}
                </span>
            </div>
            <div className="wa-log-entry-meta">
                <span className="wa-log-phone">{log.sent_to}</span>
                <span className="wa-log-dot">·</span>
                <span className="wa-log-by">{log.sent_by}</span>
                <span className="wa-log-dot">·</span>
                <span className="wa-log-time">{formatTime(log.sent_at)}</span>
            </div>
            {log?.message && (
                <div className="wa-log-reason">"{log.message}"</div>
            )}
        </div>
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function WhatsAppSendModal({ order, onClose }) {
    const [target,    setTarget]    = useState("CUSTOMER");
    const [templates, setTemplates] = useState([]);
    const [selected,  setSelected]  = useState(null);
    const [reason,    setReason]    = useState("");
    const [loadingTpl, setLoadingTpl] = useState(true);
    const [sending,   setSending]   = useState(false);
    const [error,     setError]     = useState(null);

    const [logs,        setLogs]        = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);

    const overlayRef = useRef(null);

    const loadTemplates = useCallback(async (t) => {
        setLoadingTpl(true);
        setError(null);
        setSelected(null);
        setReason("");
        try {
            const res = await getActiveWhatsappTemplate(t);
            const list = res?.templates;
            setTemplates(list);
            if (list.length > 0) setSelected(list[0]);
        } catch {
            setError("Failed to load templates.");
        } finally {
            setLoadingTpl(false);
        }
    }, []);

    const loadLogs = useCallback(async () => {
        setLoadingLogs(true);
        try {
            const res = await getWhatsappOrderLogs(order.unique_order_id);
            setLogs(res?.logs ?? []);
        } catch {
            setLogs([]);
        } finally {
            setLoadingLogs(false);
        }
    }, [order.unique_order_id]);

    useEffect(() => { loadTemplates(target); }, [target, loadTemplates]);
    // Reset to CUSTOMER if current target is no longer available (e.g. DRIVER hidden by status)
    useEffect(() => {
        const available = getTargets(order);
        if (!available.includes(target)) setTarget("CUSTOMER");
    }, [order?.orderstatus_id]); // eslint-disable-line
    useEffect(() => { loadLogs(); }, [loadLogs]);

    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onClose]);

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    const handleSend = async () => {
        if (!selected) return;
        setSending(true);
        setError(null);
        try {
            await sendManualWhatsappMsg(
                selected.id,
                order.unique_order_id,
                selected.needs_reason ? reason : null,
            );
            await loadLogs();
        } catch (err) {
            const msg = err?.response?.data?.message || "Send failed. Check Wazone server.";
            setError(msg);
            await loadLogs();
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="wa-overlay" ref={overlayRef} onClick={handleOverlayClick}>
            <div className="wa-modal">

                {/* ── Header ── */}
                <div className="wa-modal-header">
                    <div className="wa-modal-title">
                        <span className="wa-modal-icon">{WA_ICON}</span>
                        Send WhatsApp
                        <div className="wa-modal-order-pill">
                            #{order.unique_order_id?.slice(10)}
                        </div>
                    </div>
                    <div className="wa-modal-header-right">
                        <div className="wa-customer-info">
                            <span className="wa-cust-name">{order.customer_name}</span>
                            <span className="wa-cust-phone">{order.customer_phone}</span>
                        </div>
                        <button className="wa-modal-close" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* ── Two-column body ── */}
                <div className="wa-modal-body">

                    {/* ── LEFT: template picker ── */}
                    <div className="wa-left">

                        {/* Target dropdown */}
                        <div className="wa-section">
                            <div className="wa-label">Target</div>
                            <select
                                className="wa-select"
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                            >
                                {getTargets(order).map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        {/* Template list */}
                        <div className="wa-section wa-section--grow">
                            <div className="wa-label">
                                Select template
                                {!loadingTpl && (
                                    <span className="wa-tpl-count">{templates.length}</span>
                                )}
                            </div>

                            <div className="wa-template-scroll">
                                {loadingTpl ? (
                                    [...Array(3)].map((_, i) => (
                                        <div key={i} className="wa-tpl-skeleton" />
                                    ))
                                ) : templates.length === 0 ? (
                                    <div className="wa-tpl-empty">
                                        No active {target} templates.{" "}
                                        <a href="/settings/whatsapp-templates" target="_blank" rel="noreferrer">
                                            Create one →
                                        </a>
                                    </div>
                                ) : (
                                    templates.map((tpl) => (
                                        <button
                                            key={tpl.id}
                                            className={`wa-tpl-btn${selected?.id === tpl.id ? " wa-tpl-btn--active" : ""}`}
                                            onClick={() => { setSelected(tpl); setReason(""); setError(null); }}
                                        >
                                            <div className="wa-tpl-btn-top">
                                                <span className="wa-tpl-name">{tpl.name}</span>
                                                {tpl.needs_reason && (
                                                    <span className="wa-tpl-tag">+ reason</span>
                                                )}
                                            </div>
                                            {tpl.reason && (
                                                <div className="wa-tpl-hint">{tpl.reason}</div>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Reason */}
                        {selected?.needs_reason && (
                            <div className="wa-section">
                                <div className="wa-label">
                                    Reason
                                    <span className="wa-req">required</span>
                                </div>
                                <textarea
                                    className="wa-textarea"
                                    rows={2}
                                    placeholder="Enter reason for this message…"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </div>
                        )}

                        {/* ── WhatsApp-style chat preview ── */}
                        {selected && (
                            <div className="wa-section">
                                <div className="wa-label">Preview</div>
                                <div className="wa-preview">
                                    <div className="wa-preview-lbl">WhatsApp Preview</div>
                                    <div className="wa-preview-msg">
                                        <div className="wa-preview-bubble">
                                            {injectPreview(selected.body, order, reason)}
                                            <div className="wa-preview-time">
                                                {new Date().toLocaleTimeString("en-IN", {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                })}
                                                <span className="wa-preview-ticks">
                                                    {/* Double blue tick SVG */}
                                                    <svg viewBox="0 0 18 11" width="18" height="11" fill="none">
                                                        <path d="M1 5.5L4.5 9 10.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M6.5 5.5L10 9 16 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="wa-error">{error}</div>
                        )}

                        {/* Send button */}
                        <div className="wa-send-row">
                            <button className="wa-btn-cancel" onClick={onClose}>Cancel</button>
                            <button
                                className="wa-btn-send"
                                onClick={handleSend}
                                disabled={
                                    sending ||
                                    !selected ||
                                    loadingTpl ||
                                    (selected?.needs_reason && !reason.trim())
                                }
                            >
                                {sending ? (
                                    <><span className="wa-spinner" /> Sending…</>
                                ) : (
                                    <>{WA_ICON} Send Message</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* ── RIGHT: send logs ── */}
                    <div className="wa-right">
                        <div className="wa-logs-header">
                            <span className="wa-logs-title">Send History</span>
                            <span className="wa-logs-count">{logs.length}</span>
                            <button
                                className="wa-logs-refresh"
                                onClick={loadLogs}
                                disabled={loadingLogs}
                                title="Refresh logs"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                                    <path d="M1 4v6h6M23 20v-6h-6"/>
                                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
                                </svg>
                            </button>
                        </div>

                        <div className="wa-logs-scroll">
                            {loadingLogs ? (
                                [...Array(3)].map((_, i) => (
                                    <div key={i} className="wa-log-skeleton" />
                                ))
                            ) : logs.length === 0 ? (
                                <div className="wa-logs-empty">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32" style={{opacity:.3, marginBottom:8}}>
                                        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                    </svg>
                                    <p>No messages sent yet for this order</p>
                                </div>
                            ) : (
                                logs.map((log, i) => (
                                    <LogEntry key={i} log={log} />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}